import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Clock3, AlertTriangle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { revenueService, RevenueItem, RevenueFilters } from "@/services/revenue.service";
import { getCurrencySymbol } from "@/lib/utils";

const formatCurrency = (amount: number, currency: string = "USD") => {
  const formatter = new Intl.NumberFormat(
    currency.toUpperCase() === "INR" ? "en-IN" : "en-US",
    {
      style: "currency",
      currency: currency || "USD",
    }
  );
  return formatter.format(amount);
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return dateString;
  }
};

export default function Revenue() {
  const [filters, setFilters] = useState<RevenueFilters>({});
  const [revenueItems, setRevenueItems] = useState<RevenueItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    loadRevenue();
  }, [filters, currentPage, pageSize, statusFilter]);

  useEffect(() => {
    loadRevenueStats();
  }, [filters, statusFilter, selectedCurrency]);

  const loadRevenue = async () => {
    setLoading(true);
    try {
      const response = await revenueService.getRevenue({
        ...(statusFilter === "all" ? filters : { ...filters, status: statusFilter }),
        page: currentPage,
        limit: pageSize,
      });
      
      setRevenueItems(response.data);
      setTotalItems(response.total ?? response.data.length);
    } catch (error) {
      console.error("Failed to load revenue", error);
      setRevenueItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const loadRevenueStats = async () => {
    try {
      const response = await revenueService.getRevenue({
        ...(statusFilter === "all" ? filters : { ...filters, status: statusFilter }),
        page: 1,
        limit: 1000,
      });
      
      const filteredData = response.data.filter(item => 
        item.currency?.toUpperCase() === selectedCurrency.toUpperCase()
      );
      
      setStats(calculateStats(filteredData || []));
    } catch (error) {
      console.error("Failed to calculate revenue stats", error);
      setStats({ paid: 0, pending: 0, overdue: 0 });
    }
  };

  const calculateStats = (revenueData: RevenueItem[]) => {
    const filteredByCurrency = revenueData.filter(item => 
      item.currency?.toUpperCase() === selectedCurrency.toUpperCase()
    );
    
    return filteredByCurrency.reduce(
      (acc, item) => {
        const amount = item.subtotal ?? item.amount ?? 0;
        const status = (item.status || "").toLowerCase();
        if (status === "paid") {
          acc.paid += amount;
        } else if (status === "pending") {
          acc.pending += amount;
        } else if (status === "overdue") {
          acc.overdue += amount;
        } else {
          acc.pending += amount;
        }
        return acc;
      },
      { paid: 0, pending: 0, overdue: 0 }
    );
  };

  const [stats, setStats] = useState({ paid: 0, pending: 0, overdue: 0 });

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / pageSize)),
    [totalItems, pageSize]
  );

  const handleSearch = (value: string) => {
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      search: value || undefined,
    }));
  };

  const statusOptions = [
    { value: "all", label: "All statuses" },
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Pending" },
    { value: "overdue", label: "Overdue" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Revenue</h1>
          <p className="text-muted-foreground">
            View overall revenue performance and trends.
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger className="w-[120px] h-10 border-gray-300">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD $</SelectItem>
              <SelectItem value="EUR">EUR €</SelectItem>
              <SelectItem value="GBP">GBP £</SelectItem>
              <SelectItem value="INR">INR ₹</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid Revenue</CardTitle>
            <span className="text-lg font-semibold text-muted-foreground">
              {getCurrencySymbol(selectedCurrency)}
            </span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.paid, selectedCurrency)}
            </div>
            <p className="text-xs text-muted-foreground">Revenue received</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Revenue</CardTitle>
            <Clock3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.pending, selectedCurrency)}
            </div>
            <p className="text-xs text-muted-foreground">Awaiting payment</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue Revenue</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.overdue, selectedCurrency)}
            </div>
            <p className="text-xs text-muted-foreground">Past due revenue</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Revenue Details</CardTitle>
              <CardDescription>All transactions contributing to revenue</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search Revenue..."
                  className="pl-8"
                  defaultValue={filters.search || ""}
                  onChange={(event) => handleSearch(event.target.value)}
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => {
                  setPageSize(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Rows per page" />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 25, 50].map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size} rows
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading revenue data...
                    </TableCell>
                  </TableRow>
                ) : revenueItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      No revenue data found
                    </TableCell>
                  </TableRow>
                ) : (
                  revenueItems.map((item) => {
                    const displayDate = item.date || item.invoiceDate || "";
                    const customerName = item.clientName || item.customer?.name || "Unknown Customer";
                    const amount = item.subtotal ?? item.amount ?? 0;
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.invoiceNumber || "-"}
                        </TableCell>
                        <TableCell>
                          {customerName}
                        </TableCell>
                        <TableCell>{displayDate ? formatDate(displayDate) : "-"}</TableCell>
                        <TableCell>{item.dueDate ? formatDate(item.dueDate) : "-"}</TableCell>
                        <TableCell>{formatCurrency(amount, item.currency)}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              item.status === "paid"
                                ? "bg-green-100 text-green-800 hover:bg-green-100"
                                : item.status === "pending"
                                ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
                                : item.status === "overdue"
                                ? "bg-red-100 text-red-800 hover:bg-red-100"
                                : "bg-gray-100 text-gray-800 hover:bg-gray-100"
                            }
                          >
                            {item.status
                              ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                              : "-"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {revenueItems.length > 0 && (
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, totalItems)} of{" "}
                {totalItems} entries
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="rounded-xl"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const pageNumber = index + 1;
                    return (
                      <Button
                        key={pageNumber}
                        variant={pageNumber === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNumber)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {pageNumber}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((page) => Math.min(totalPages, page + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-xl"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
